import {
  OpenAIRealtimeWebRTC,
  type OpenAIRealtimeWebRTCOptions,
  type RealtimeTransportLayerConnectOptions,
  type TransportEvent,
  type TransportLayerTranscriptDelta,
} from '@openai/agents/realtime';
import { z } from 'zod';
import { AuthoritativeCloseSchema, GeminiSessionIssuanceSchema, GeminiServerMessageSchema, GeminiClientMessageSchema, GEMINI_MAX_FRAME_BYTES, type GeminiSessionIssuance, type GeminiServerMessage } from '@merlin/project-schema/live-session';
import { GeminiBrowserAudio, PlaybackQueueExceededError, decodePcm, encodePcm, type GeminiAudioLike } from './gemini-audio.js';
import { serverJsonEventSchema } from './control-events.js';

const REALTIME_MODEL = 'gpt-realtime-2.1' as const;
const REALTIME_VOICE = 'alloy' as const;

// Gemini capture batches PCM16 mono at 16 kHz into 640-byte (20 ms) frames (see
// PcmCapture in gemini-audio.ts). Muting mid-utterance pads ~1 s of generated
// silence through that same frame size/pacing before the final `audio_stream_end`
// so Gemini's automatic VAD has real end-of-speech audio to finalise the caller's
// cut-off turn (live 3.8 evidence: an abrupt `audio_stream_end` with no trailing
// silence left 2/2 runs with no completed turn; ~1 s of paced silence produced a
// reply in 3/3 runs).
const GEMINI_FRAME_BYTES = 640;
const GEMINI_FRAME_MS = 20;
const MUTE_SILENCE_PAD_MS = 1000;
const MUTE_SILENCE_PAD_FRAMES = Math.round(MUTE_SILENCE_PAD_MS / GEMINI_FRAME_MS);
const MUTE_SILENCE_FRAME = new Uint8Array(GEMINI_FRAME_BYTES);

export const createSessionResponseSchema = z.object({
  sessionId: z.string().uuid(),
  controlToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  webrtcToken: z.object({
    value: z.string().regex(/^ek_[A-Za-z0-9_-]{43}$/),
    expiresAt: z.number().int().positive(),
  }).strict(),
  model: z.literal(REALTIME_MODEL),
}).strict();

const sidebandReadySchema = z.object({
  type: z.literal('sideband_ready'),
  sessionId: z.string().uuid(),
}).strict();

const identityAcceptedSchema = z.object({
  type: z.literal('identity_accepted'),
}).passthrough();

const controlErrorSchema = z.object({
  type: z.literal('error'),
  message: z.string().min(1).max(500),
}).passthrough();
const runtimeUsageSettledSchema = z.object({
  type: z.literal('event'),
  kind: z.literal('runtime_usage_settled'),
  payload: z.object({
    kind: z.enum(['responses', 'realtime_response', 'input_transcription']),
    finalStatus: z.string().min(1).max(80),
    usageStatus: z.enum(['reported', 'missing']),
    order: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    sessionId: z.string().regex(/^[a-f0-9]{64}$/),
    responseId: z.string().regex(/^[a-f0-9]{64}$/),
    itemId: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    totalTokens: z.number().int().nonnegative().optional(),
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    cachedInputTokens: z.number().int().nonnegative().optional(),
    cachedTextInputTokens: z.number().int().nonnegative().optional(),
    cachedAudioInputTokens: z.number().int().nonnegative().optional(),
    cachedImageInputTokens: z.number().int().nonnegative().optional(),
    cacheWriteTokens: z.number().int().nonnegative().optional(),
    textInputTokens: z.number().int().nonnegative().optional(),
    textOutputTokens: z.number().int().nonnegative().optional(),
    audioInputTokens: z.number().int().nonnegative().optional(),
    audioOutputTokens: z.number().int().nonnegative().optional(),
    imageInputTokens: z.number().int().nonnegative().optional(),
    imageOutputTokens: z.number().int().nonnegative().optional(),
    reasoningTokens: z.number().int().nonnegative().optional(),
    audioInputDurationSeconds: z.number().finite().nonnegative().optional(),
  }).strict(),
}).strict();
export type CreateSessionResponse = z.infer<typeof createSessionResponseSchema>;
const publicSessionResponseSchema = z.union([createSessionResponseSchema, GeminiSessionIssuanceSchema]);
export type GeminiSemanticEvent = Extract<GeminiServerMessage, { type: 'transcript' | 'interrupted' | 'turn_complete' | 'interaction_status' }> | { type: 'playback_state'; playing: boolean };

export const authoritativeCloseSchema = AuthoritativeCloseSchema;
export type AuthoritativeClose = z.infer<typeof authoritativeCloseSchema>;

export type MerlinVariant = 'sales' | 'intake';
export type BrowserRealtimeState = 'idle' | 'connecting' | 'ready' | 'closing' | 'closed';
export type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected';

export interface RealtimeTransportLike {
  readonly callId: string | undefined;
  connect(options: RealtimeTransportLayerConnectOptions): Promise<void>;
  mute(muted: boolean): void;
  close(): void;
  on(event: string, listener: (...args: any[]) => void): unknown;
  off?(event: string, listener: (...args: any[]) => void): unknown;
}

export type RealtimeTransportFactory = (
  options: OpenAIRealtimeWebRTCOptions,
) => RealtimeTransportLike;

export interface ControlSocketLike {
  readonly readyState: number;
  readonly bufferedAmount?: number;
  addEventListener(type: string, listener: (event: any) => void): void;
  removeEventListener(type: string, listener: (event: any) => void): void;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface BrowserRealtimeDependencies {
  fetch: typeof fetch;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  createControlSocket(url: string): ControlSocketLike;
  createTransport: RealtimeTransportFactory;
  createGeminiAudio?(started: (sequence: number) => void, stopped: (sequence: number) => void): GeminiAudioLike;
}

export interface BrowserRealtimeEvents {
  onConnectionChange?(status: RealtimeConnectionStatus): void;
  onTranscriptDelta?(event: TransportLayerTranscriptDelta): void;
  onRawEvent?(event: TransportEvent): void;
  onControlMessage?(message: unknown): void;
  onRuntimeUsageSettled?(payload: z.infer<typeof runtimeUsageSettledSchema>['payload']): void;
  onControlClose?(): void;
  onSemanticEvent?(event: GeminiSemanticEvent): void;
}

export interface BrowserRealtimeControllerOptions {
  variant: MerlinVariant;
  timezone: string;
  clientInstanceId: string;
  audioElement: HTMLAudioElement;
  sessionEndpoint?: string;
  realtimeCallEndpoint?: string;
  controlSocketUrl?: string;
  setupTimeoutMs?: number;
  closeTimeoutMs?: number;
  issueSession?(signal: AbortSignal): Promise<CreateSessionResponse>;
  realtimeEvents?: BrowserRealtimeEvents;
  dependencies?: Partial<BrowserRealtimeDependencies>;
}

export interface BrowserRealtimeConnection {
  sessionId: string;
  callId?: string;
}

const defaultDependencies: BrowserRealtimeDependencies = {
  fetch: (...args) => globalThis.fetch(...args),
  getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
  createControlSocket: (url) => new WebSocket(url),
  createTransport: (options) => new OpenAIRealtimeWebRTC(options),
  createGeminiAudio: (started, stopped) => new GeminiBrowserAudio(started, stopped),
};

function defaultControlSocketUrl(): string {
  const url = new URL('/ws', browserLocationHref());
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function browserLocationHref(): string {
  return typeof window === 'undefined' ? 'http://localhost/' : window.location.href;
}

function parseJsonMessage(event: MessageEvent<unknown>): unknown {
  if (typeof event.data !== 'string') return undefined;
  try {
    return JSON.parse(event.data) as unknown;
  } catch {
    return undefined;
  }
}

function dispatchControlMessage(
  message: unknown,
  events: BrowserRealtimeEvents | undefined,
  onIdentityAccepted?: () => void,
  onRuntimeUsageSettled?: (message: unknown) => void,
): void {
  if (message === undefined) return;
  if (identityAcceptedSchema.safeParse(message).success) onIdentityAccepted?.();
  if (isRuntimeUsageSettledEnvelope(message)) {
    onRuntimeUsageSettled?.(message);
    return;
  }
  events?.onControlMessage?.(message);
}

function isRuntimeUsageSettledEnvelope(message: unknown): boolean {
  if (!message || typeof message !== 'object') return false;
  const value = message as Record<string, unknown>;
  return value.type === 'event' && value.kind === 'runtime_usage_settled';
}

export class BrowserRealtimeController {
  private readonly dependencies: BrowserRealtimeDependencies;
  private readonly options: Required<Pick<
    BrowserRealtimeControllerOptions,
    'sessionEndpoint' | 'realtimeCallEndpoint' | 'setupTimeoutMs' | 'closeTimeoutMs'
  >> & BrowserRealtimeControllerOptions;
  private mediaStream?: MediaStream;
  private transport?: RealtimeTransportLike;
  private controlSocket?: ControlSocketLike;
  private currentState: BrowserRealtimeState = 'idle';
  private connection?: BrowserRealtimeConnection;
  private connectPromise?: Promise<BrowserRealtimeConnection>;
  private closePromise?: Promise<AuthoritativeClose>;
  private setupAbortController?: AbortController;
  private setupTimeout?: ReturnType<typeof globalThis.setTimeout>;
  private mediaReadySent = false;
  private geminiAudio?: GeminiAudioLike;
  private selectedProvider: 'openai' | 'gemini' = 'openai';
  private transportEpoch = 1;
  private playbackEpoch = 1;
  private inputSequence = 0;
  private outputSequence = 0;
  private recovering = false;
  private captureStopped = false;
  private muted = false;
  private audioSinceStreamEnd = false;
  private mutePadTimer?: ReturnType<typeof globalThis.setTimeout>;
  private mutePadFramesSent = 0;
  private readonly socketListeners: Array<{ event: string; listener: (event: any) => void }> = [];
  private runtimeUsageSessionId?: string;
  private readonly runtimeUsageOrders = new Set<number>();
  private readonly transportListeners: Array<{
    event: string;
    listener: (...args: any[]) => void;
  }> = [];

  constructor(options: BrowserRealtimeControllerOptions) {
    this.options = {
      ...options,
      sessionEndpoint: options.sessionEndpoint ?? '/api/realtime/session',
      realtimeCallEndpoint: options.realtimeCallEndpoint ??
        new URL('/api/realtime/call', window.location.href).toString(),
      // WebRTC negotiation can take several seconds before the SDK exposes the
      // call ID, and Merlin then needs time to attach its authenticated server
      // sideband. Keep this below the ephemeral-key lifetime, but do not tear
      // down a healthy call while the sideband handshake is still in flight.
      setupTimeoutMs: options.setupTimeoutMs ?? 30_000,
      closeTimeoutMs: options.closeTimeoutMs ?? 7_000,
    };
    this.dependencies = { ...defaultDependencies, ...options.dependencies };
  }

  get state(): BrowserRealtimeState {
    return this.currentState;
  }
  get provider(): 'openai' | 'gemini' { return this.selectedProvider; }

  get callId(): string | undefined {
    return this.connection?.callId;
  }

  get sessionId(): string | undefined {
    return this.connection?.sessionId;
  }

  connect(): Promise<BrowserRealtimeConnection> {
    if (this.connection) return Promise.resolve(this.connection);
    if (this.connectPromise) return this.connectPromise;
    if (this.currentState === 'closed') {
      return Promise.reject(new Error('Realtime controller is closed'));
    }

    this.currentState = 'connecting';
    // Reserve/resume the context before the first await while Start retains user activation.
    if (this.options.variant === 'intake' && !this.options.issueSession) {
      try { this.prepareGeminiAudio(); } catch (error) { this.currentState = 'closed'; return Promise.reject(error); }
    }
    const setupAbortController = new AbortController();
    this.setupAbortController = setupAbortController;
    this.setupTimeout = globalThis.setTimeout(() => {
      setupAbortController.abort(new Error('Realtime setup timed out'));
    }, this.options.setupTimeoutMs);
    this.connectPromise = this.connectInternal(setupAbortController.signal)
      .catch((error: unknown) => {
        this.currentState = 'closed';
        this.disposeResources();
        throw error;
      })
      .finally(() => {
        if (this.setupTimeout !== undefined) {
          globalThis.clearTimeout(this.setupTimeout);
          this.setupTimeout = undefined;
        }
        if (this.setupAbortController === setupAbortController) {
          this.setupAbortController = undefined;
        }
      });
    return this.connectPromise;
  }

  close(reason?: string): Promise<AuthoritativeClose> {
    if (this.closePromise) return this.closePromise;
    const socket = this.controlSocket;
    if (this.currentState !== 'ready' || socket?.readyState !== WebSocket.OPEN) {
      this.currentState = 'closed';
      this.setupAbortController?.abort(new Error('Realtime setup cancelled'));
      this.disposeResources();
      return Promise.reject(new Error('Authoritative close is unavailable before attachment'));
    }

    this.currentState = 'closing';
    if (this.selectedProvider === 'gemini') this.stopLocalCapture();
    this.closePromise = new Promise<AuthoritativeClose>((resolve, reject) => {
      let settled = false;
      const timeout = globalThis.setTimeout(() => {
        finish(new Error('Authoritative close timed out'));
      }, this.options.closeTimeoutMs);
      const cleanup = () => {
        globalThis.clearTimeout(timeout);
        socket.removeEventListener('message', onMessage);
        socket.removeEventListener('error', onError);
        socket.removeEventListener('close', onClose);
      };
      const finish = (result: AuthoritativeClose | Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        this.currentState = 'closed';
        this.disposeResources();
        if (result instanceof Error) reject(result);
        else resolve(result);
      };
      const onMessage = (event: MessageEvent<unknown>) => {
        const acknowledged = authoritativeCloseSchema.safeParse(parseJsonMessage(event));
        if (acknowledged.success) finish(acknowledged.data);
      };
      const onError = () => finish(new Error('Control socket failed before close acknowledgement'));
      const onClose = () => finish(new Error('Control socket closed before close acknowledgement'));

      socket.addEventListener('message', onMessage);
      socket.addEventListener('error', onError);
      socket.addEventListener('close', onClose);
      const normalisedReason = reason?.trim().slice(0, 160);
      try {
        const message = {
          type: 'close',
          ...(normalisedReason ? { reason: normalisedReason } : {}),
        };
        if (this.selectedProvider === 'gemini') this.sendGemini(message);
        else socket.send(JSON.stringify(message));
      } catch (error: unknown) {
        finish(error instanceof Error ? error : new Error('Could not request authoritative close'));
      }
    });
    return this.closePromise;
  }

  stopLocalCapture(): void {
    this.cancelMutePadding();
    this.captureStopped = true;
    this.geminiAudio?.stopCapture();
    this.transport?.mute(true);
    for (const track of this.mediaStream?.getTracks() ?? []) track.stop();
    this.mediaStream = undefined;
  }

  /**
   * Caller-facing microphone mute for the Gemini/Intake transport only; a no-op for the
   * OpenAI/Sales transport. Immediately: tracks are disabled and worklet capture stops, so
   * no real microphone sample leaves the browser after the click. If audio was sent since
   * the last `audio_stream_end` (and the call is ready and not recovering), muting then
   * paces ~1 s of generated digital silence out through the same `audio_input` path (same
   * frame size, contiguous sequence numbers) before sending exactly one `audio_stream_end` —
   * Gemini's automatic VAD needs real end-of-speech audio to finalise a cut-off turn; sending
   * `audio_stream_end` alone left it with no completed turn. Unmuting mid-pad cancels the
   * remaining silence frames and sends no `audio_stream_end` (real audio resumes the stream);
   * recovery, socket close, or the transport epoch changing cancel it the same way. A second
   * mute click while padding is in flight is already a no-op via the guard above. Unmuting
   * re-enables tracks/capture so the next `audio_input` reopens the stream at the next
   * sequence, contiguous with any padding already sent.
   */
  setMicrophoneMuted(muted: boolean): void {
    if (this.selectedProvider !== 'gemini' || this.captureStopped || this.muted === muted) return;
    this.muted = muted;
    if (muted) {
      for (const track of this.mediaStream?.getAudioTracks() ?? []) track.enabled = false;
      this.geminiAudio?.setCapturing(false);
      if (this.audioSinceStreamEnd && this.currentState === 'ready' && !this.recovering) {
        this.startMutePadding();
      }
      return;
    }
    this.cancelMutePadding();
    if (this.mediaReadySent && !this.recovering) {
      for (const track of this.mediaStream?.getAudioTracks() ?? []) track.enabled = true;
      this.geminiAudio?.setCapturing(true);
    }
  }

  /**
   * Paces `MUTE_SILENCE_PAD_FRAMES` zero-valued PCM frames out through the normal
   * `sendGemini`/`audio_input` path (contiguous `inputSequence`, current `transportEpoch`),
   * one per `GEMINI_FRAME_MS`, then sends `audio_stream_end`. These frames are generated
   * internally and sent directly through `sendGemini` rather than the capture callback, so
   * they intentionally bypass that callback's `muted` guard — which keeps blocking every real
   * microphone frame for the whole duration. Aborts (no `audio_stream_end`, no further
   * frames) if unmuted, recovery starts, the transport epoch changes, or the call closes.
   */
  private startMutePadding(): void {
    const epoch = this.transportEpoch;
    this.mutePadFramesSent = 0;
    const sendNextFrame = (): void => {
      this.mutePadTimer = undefined;
      if (!this.muted || this.recovering || this.currentState === 'closed' || this.transportEpoch !== epoch) return;
      try {
        this.sendGemini({
          type: 'audio_input',
          transportEpoch: this.transportEpoch,
          sequence: this.inputSequence++,
          data: encodePcm(MUTE_SILENCE_FRAME),
        });
      } catch { this.failGemini(); return; }
      this.mutePadFramesSent++;
      if (this.mutePadFramesSent < MUTE_SILENCE_PAD_FRAMES) {
        this.mutePadTimer = globalThis.setTimeout(sendNextFrame, GEMINI_FRAME_MS);
        return;
      }
      this.audioSinceStreamEnd = false;
      try { this.sendGemini({ type: 'audio_stream_end', transportEpoch: this.transportEpoch }); }
      catch { this.failGemini(); }
    };
    this.mutePadTimer = globalThis.setTimeout(sendNextFrame, GEMINI_FRAME_MS);
  }

  private cancelMutePadding(): void {
    if (this.mutePadTimer === undefined) return;
    globalThis.clearTimeout(this.mutePadTimer);
    this.mutePadTimer = undefined;
  }

  sendControlMessage(message: unknown): void {
    if (this.selectedProvider === 'gemini') {
      // The public UI owns contact confirmation and revocation; media/close are controller-owned.
      const parsed = GeminiClientMessageSchema.parse(message);
      if (parsed.type !== 'caller_details_provided' && parsed.type !== 'contact_details_editing') throw new Error('Unsupported browser control message');
      try { this.sendGemini(parsed); } catch (error) { this.failGemini(); throw error; }
      return;
    }
    if (this.controlSocket?.readyState !== WebSocket.OPEN) {
      throw new Error('Merlin control socket is not ready');
    }
    this.controlSocket.send(JSON.stringify(message));
  }

  private async connectInternal(signal: AbortSignal): Promise<BrowserRealtimeConnection> {
    this.mediaStream = await this.awaitSetup(
      this.dependencies.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      }),
      signal,
      (lateStream) => {
        for (const track of lateStream.getTracks()) track.stop();
      },
    );
    const audioTracks = this.mediaStream.getAudioTracks();
    if (audioTracks.length === 0) {
      throw new Error('Microphone did not provide an audio track');
    }
    for (const track of audioTracks) track.enabled = false;

    const pending = await this.createPendingSession(signal);
    if ('provider' in pending) {
      this.selectedProvider = pending.provider;
      return this.connectGemini(pending, signal);
    }
    this.geminiAudio?.dispose(); this.geminiAudio = undefined;
    const realtimeCallUrl = new URL(this.options.realtimeCallEndpoint, browserLocationHref());
    realtimeCallUrl.searchParams.set('sessionId', pending.sessionId);
    this.transport = this.dependencies.createTransport({
      mediaStream: this.mediaStream,
      audioElement: this.options.audioElement,
      baseUrl: realtimeCallUrl.toString(),
    });
    this.bindTransportEvents(this.transport);
    this.transport.mute(true);
    await this.awaitSetup(
      this.transport.connect({
        apiKey: pending.webrtcToken.value,
        model: REALTIME_MODEL,
        initialSessionConfig: {
          outputModalities: ['audio'],
          parallelToolCalls: false,
          audio: {
            input: {
              transcription: { model: 'gpt-4o-mini-transcribe' },
              turnDetection: {
                type: 'server_vad',
                threshold: 0.5,
                prefixPaddingMs: 300,
                silenceDurationMs: 500,
                createResponse: false,
                interruptResponse: true,
              },
            },
            output: { voice: REALTIME_VOICE },
          },
        },
      }),
      signal,
    );

    const callId = this.transport.callId;
    if (!callId || !/^rtc_[A-Za-z0-9_-]{8,252}$/.test(callId)) {
      throw new Error('OpenAI call ID was not available after WebRTC connected');
    }

    this.controlSocket = this.dependencies.createControlSocket(
      this.options.controlSocketUrl ?? defaultControlSocketUrl(),
    );
    await this.waitForSocketOpen(this.controlSocket, signal);
    const sidebandReady = this.waitForSidebandReady(
      this.controlSocket,
      pending.sessionId,
      signal,
    );
    this.controlSocket.send(JSON.stringify({
      type: 'attach',
      sessionId: pending.sessionId,
      controlToken: pending.controlToken,
      callId,
    }));
    await sidebandReady;

    this.connection = { sessionId: pending.sessionId, callId };
    this.currentState = 'ready';
    this.bindControlLifecycle(this.controlSocket);
    return this.connection;
  }

  private async createPendingSession(signal: AbortSignal) {
    if (this.options.issueSession) {
      const issued = await this.awaitSetup(this.options.issueSession(signal), signal);
      return createSessionResponseSchema.parse(issued);
    }
    const response = await this.awaitSetup(
      this.dependencies.fetch(this.options.sessionEndpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          variant: this.options.variant,
          timezone: this.options.timezone,
          clientInstanceId: this.options.clientInstanceId,
        }),
        credentials: 'same-origin',
        signal,
      }),
      signal,
    );
    if (!response.ok) {
      throw new Error(`Merlin session request failed (${response.status})`);
    }
    return publicSessionResponseSchema.parse(await this.awaitSetup(response.json(), signal));
  }

  private prepareGeminiAudio(): void {
    this.geminiAudio ??= (this.dependencies.createGeminiAudio ?? defaultDependencies.createGeminiAudio!)(
      (sequence) => {
        if (this.recovering || this.currentState === 'closed') return;
        try {
          this.sendGemini({ type: 'playout_started', transportEpoch: this.transportEpoch, playbackEpoch: this.playbackEpoch, sequence });
          this.options.realtimeEvents?.onSemanticEvent?.({ type: 'playback_state', playing: true });
        } catch { this.failGemini(); }
      },
      (throughSequence) => {
        if (this.recovering || this.currentState === 'closed') return;
        try {
          this.sendGemini({ type: 'playout_stopped', transportEpoch: this.transportEpoch, playbackEpoch: this.playbackEpoch, throughSequence });
          this.options.realtimeEvents?.onSemanticEvent?.({ type: 'playback_state', playing: false });
        } catch { this.failGemini(); }
      },
    );
  }

  private async connectGemini(pending: GeminiSessionIssuance, signal: AbortSignal): Promise<BrowserRealtimeConnection> {
    this.prepareGeminiAudio();
    await this.awaitSetup(this.geminiAudio!.start(this.mediaStream!, (pcm) => {
      if (!this.mediaReadySent || this.recovering || this.captureStopped || this.muted || this.currentState !== 'ready') return;
      try {
        this.sendGemini({ type: 'audio_input', transportEpoch: this.transportEpoch, sequence: this.inputSequence++, data: encodePcm(pcm) });
        this.audioSinceStreamEnd = true;
      } catch { this.failGemini(); }
    }), signal);
    const url = new URL(pending.websocketPath, browserLocationHref());
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = this.controlSocket = this.dependencies.createControlSocket(url.toString());
    await this.waitForSocketOpen(socket, signal);
    let ready = false;
    await this.awaitSetup(new Promise<void>((resolve, reject) => {
      const fail = () => { reject(new Error('Gemini connection failed')); this.failGemini(); };
      const listen = (event: string, listener: (event: any) => void) => { socket.addEventListener(event, listener); this.socketListeners.push({ event, listener }); };
      listen('error', fail); listen('close', fail);
      listen('message', (event: MessageEvent<unknown>) => {
        try {
          if (typeof event.data !== 'string' || new TextEncoder().encode(event.data).length > GEMINI_MAX_FRAME_BYTES) throw new Error('Invalid Gemini frame');
          const message = JSON.parse(event.data) as unknown;
          const parsed = GeminiServerMessageSchema.safeParse(message);
          if (parsed.success && parsed.data.type === 'session_ready') {
            if (ready || parsed.data.sessionId !== pending.sessionId) throw new Error('Unexpected Gemini readiness');
            ready = true; this.connection = { sessionId: pending.sessionId }; this.currentState = 'ready';
            if (!this.captureStopped) {
              this.mediaReadySent = true;
              this.sendGemini({ type: 'media_ready', transportEpoch: this.transportEpoch });
              if (!this.muted) {
                for (const track of this.mediaStream?.getAudioTracks() ?? []) track.enabled = true;
                this.geminiAudio!.setCapturing(true);
              }
            }
            resolve(); return;
          }
          if (!ready) throw new Error('Gemini message before readiness');
          this.handleGeminiMessage(message);
        } catch (error) {
          if (error instanceof PlaybackQueueExceededError) this.failGemini('client_playback_overflow');
          fail();
        }
      });
      try { this.sendGemini({ type: 'attach', sessionId: pending.sessionId, controlToken: pending.controlToken }); }
      catch { fail(); }
    }), signal);
    return this.connection!;
  }

  private sendGemini(message: unknown): void {
    const socket = this.controlSocket;
    if (socket?.readyState !== WebSocket.OPEN || (socket.bufferedAmount ?? 0) > 256 * 1024) throw new Error('Gemini socket unavailable');
    const encoded = JSON.stringify(GeminiClientMessageSchema.parse(message));
    const bytes = new TextEncoder().encode(encoded).length;
    if (bytes > GEMINI_MAX_FRAME_BYTES || (socket.bufferedAmount ?? 0) + bytes > 256 * 1024) throw new Error('Gemini frame exceeded');
    socket.send(encoded);
  }

  private handleGeminiMessage(value: unknown): void {
    const message = GeminiServerMessageSchema.safeParse(value);
    if (message.success) {
      const event = message.data;
      if (event.type === 'transport_state') {
        if (event.state === 'reconnecting') {
          if (this.recovering || event.transportEpoch !== this.transportEpoch) throw new Error('Invalid recovery epoch');
          this.cancelMutePadding();
          this.recovering = true; this.geminiAudio!.setCapturing(false); this.geminiAudio!.clearPlayback();
          for (const track of this.mediaStream?.getAudioTracks() ?? []) track.enabled = false;
          this.options.realtimeEvents?.onSemanticEvent?.({ type: 'playback_state', playing: false });
          this.options.realtimeEvents?.onConnectionChange?.('connecting');
        } else {
          if (!this.recovering || event.transportEpoch !== this.transportEpoch + 1) throw new Error('Invalid recovery epoch');
          this.transportEpoch = event.transportEpoch; this.playbackEpoch = 1; this.inputSequence = 0; this.outputSequence = 0; this.recovering = false; this.audioSinceStreamEnd = false;
          if (this.mediaReadySent && !this.captureStopped) {
            this.sendGemini({ type: 'media_ready', transportEpoch: this.transportEpoch });
            if (!this.muted) {
              for (const track of this.mediaStream?.getAudioTracks() ?? []) track.enabled = true;
              this.geminiAudio!.setCapturing(true);
            }
          }
          this.options.realtimeEvents?.onConnectionChange?.('connected');
        }
      } else if (event.type === 'audio_output') {
        if (this.recovering || event.transportEpoch !== this.transportEpoch || event.playbackEpoch !== this.playbackEpoch || event.sequence !== this.outputSequence++) throw new Error('Invalid output sequence');
        this.geminiAudio!.enqueue(decodePcm(event.data), event.sequence);
      } else if (event.type === 'interrupted') {
        if (this.recovering || event.transportEpoch !== this.transportEpoch || event.playbackEpoch !== this.playbackEpoch + 1) throw new Error('Invalid interruption epoch');
        this.geminiAudio!.clearPlayback(); this.playbackEpoch = event.playbackEpoch; this.outputSequence = 0;
        this.options.realtimeEvents?.onSemanticEvent?.(event);
      } else if (event.type !== 'session_ready') this.options.realtimeEvents?.onSemanticEvent?.(event);
      else throw new Error('Duplicate Gemini readiness');
      return;
    }
    if (authoritativeCloseSchema.safeParse(value).success) return;
    if (runtimeUsageSettledSchema.safeParse(value).success) { this.handleRuntimeUsageSettled(value); return; }
    const control = serverJsonEventSchema.parse(value);
    if (control.type !== 'identity_accepted' && control.type !== 'event' && control.type !== 'error') throw new Error('Unexpected Gemini control event');
    this.options.realtimeEvents?.onControlMessage?.(control);
  }

  private failGemini(reason?: 'client_playback_overflow'): void {
    if (this.currentState === 'closed') return;
    const wasAttached = this.currentState === 'ready' || this.currentState === 'closing';
    this.currentState = 'closed'; this.disposeResources(reason);
    if (wasAttached) this.options.realtimeEvents?.onControlClose?.();
  }

  private waitForSocketOpen(socket: ControlSocketLike, signal: AbortSignal): Promise<void> {
    if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onError);
        socket.removeEventListener('close', onClose);
      };
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Merlin control socket failed to open'));
      };
      const onClose = () => {
        cleanup();
        reject(new Error('Merlin control socket closed before attachment'));
      };
      const onAbort = () => {
        cleanup();
        reject(this.abortReason(signal));
      };
      socket.addEventListener('open', onOpen);
      socket.addEventListener('error', onError);
      socket.addEventListener('close', onClose);
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  }

  private waitForSidebandReady(
    socket: ControlSocketLike,
    sessionId: string,
    signal: AbortSignal,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        socket.removeEventListener('message', onMessage);
        socket.removeEventListener('error', onError);
        socket.removeEventListener('close', onClose);
      };
      const onMessage = (event: MessageEvent<unknown>) => {
        const message = parseJsonMessage(event);
        const ready = sidebandReadySchema.safeParse(message);
        if (ready.success && ready.data.sessionId === sessionId) {
          cleanup();
          resolve();
          return;
        }
        const error = controlErrorSchema.safeParse(message);
        if (error.success) {
          cleanup();
          reject(new Error(error.data.message));
          return;
        }
        dispatchControlMessage(
          message,
          this.options.realtimeEvents,
          undefined,
          (runtimeUsageMessage) => this.handleRuntimeUsageSettled(runtimeUsageMessage),
        );
      };
      const onError = () => {
        cleanup();
        reject(new Error('Merlin control socket failed during attachment'));
      };
      const onClose = () => {
        cleanup();
        reject(new Error('Merlin control socket closed during attachment'));
      };
      const onAbort = () => {
        cleanup();
        reject(this.abortReason(signal));
      };
      socket.addEventListener('message', onMessage);
      socket.addEventListener('error', onError);
      socket.addEventListener('close', onClose);
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  }

  private awaitSetup<T>(
    operation: Promise<T>,
    signal: AbortSignal,
    onLateResolve?: (value: T) => void,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => signal.removeEventListener('abort', onAbort);
      const onAbort = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(this.abortReason(signal));
      };

      operation.then(
        (value) => {
          if (settled) {
            onLateResolve?.(value);
            return;
          }
          settled = true;
          cleanup();
          resolve(value);
        },
        (error: unknown) => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(error);
        },
      );

      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    });
  }

  private abortReason(signal: AbortSignal): Error {
    return signal.reason instanceof Error
      ? signal.reason
      : new Error('Realtime setup cancelled');
  }

  private bindTransportEvents(transport: RealtimeTransportLike): void {
    this.addTransportListener(transport, 'connection_change', (status: RealtimeConnectionStatus) => {
      this.options.realtimeEvents?.onConnectionChange?.(status);
      if (status === 'disconnected' && this.currentState === 'ready') {
        this.currentState = 'closed';
        this.disposeResources();
        this.options.realtimeEvents?.onControlClose?.();
      }
    });
    this.addTransportListener(
      transport,
      'audio_transcript_delta',
      (event: TransportLayerTranscriptDelta) => {
        this.options.realtimeEvents?.onTranscriptDelta?.(event);
      },
    );
    this.addTransportListener(transport, '*', (event: TransportEvent) => {
      const type = (event as { type?: unknown }).type;
      if (this.controlSocket?.readyState === WebSocket.OPEN) {
        if (type === 'output_audio_buffer.started') {
          this.controlSocket.send(JSON.stringify({ type: 'playout_started' }));
        } else if (type === 'output_audio_buffer.stopped') {
          this.controlSocket.send(JSON.stringify({ type: 'playout_stopped' }));
        }
      }
      this.options.realtimeEvents?.onRawEvent?.(event);
    });
  }

  private addTransportListener(
    transport: RealtimeTransportLike,
    event: string,
    listener: (...args: any[]) => void,
  ): void {
    transport.on(event, listener);
    this.transportListeners.push({ event, listener });
  }

  private bindControlLifecycle(socket: ControlSocketLike): void {
    socket.addEventListener('message', (event: MessageEvent<unknown>) => {
      const message = parseJsonMessage(event);
      dispatchControlMessage(
        message,
        this.options.realtimeEvents,
        () => this.releaseIdentityGatedMedia(),
        (runtimeUsageMessage) => this.handleRuntimeUsageSettled(runtimeUsageMessage),
      );
    });
    socket.addEventListener('close', () => {
      if (this.currentState === 'closed') return;
      this.options.realtimeEvents?.onControlClose?.();
      this.disposeResources();
      this.currentState = 'closed';
    });
  }

  private releaseIdentityGatedMedia(): void {
    if (
      this.mediaReadySent || this.currentState !== 'ready' || !this.transport ||
      this.controlSocket?.readyState !== WebSocket.OPEN
    ) return;
    this.mediaReadySent = true;
    this.transport.mute(false);
    this.controlSocket.send(JSON.stringify({ type: 'media_ready' }));
  }

  private handleRuntimeUsageSettled(message: unknown): void {
    const runtimeUsage = runtimeUsageSettledSchema.safeParse(message);
    if (!runtimeUsage.success) return;
    const payload = runtimeUsage.data.payload;
    if (this.runtimeUsageSessionId === undefined) this.runtimeUsageSessionId = payload.sessionId;
    else if (payload.sessionId !== this.runtimeUsageSessionId) return;
    if (this.runtimeUsageOrders.has(payload.order)) return;
    this.runtimeUsageOrders.add(payload.order);
    this.options.realtimeEvents?.onRuntimeUsageSettled?.(payload);
  }

  private disposeResources(geminiFailure?: 'client_playback_overflow'): void {
    this.cancelMutePadding();
    this.muted = false; this.audioSinceStreamEnd = false;
    this.geminiAudio?.dispose(); this.geminiAudio = undefined;
    if (this.controlSocket) for (const { event, listener } of this.socketListeners) this.controlSocket.removeEventListener(event, listener);
    this.socketListeners.length = 0;
    if (this.transport) {
      for (const { event, listener } of this.transportListeners) {
        this.transport.off?.(event, listener);
      }
      this.transportListeners.length = 0;
      this.transport.close();
      this.transport = undefined;
    }
    if (this.controlSocket && this.controlSocket.readyState < WebSocket.CLOSING) {
      if (geminiFailure) this.controlSocket.close(4000, geminiFailure);
      else this.controlSocket.close();
    }
    this.controlSocket = undefined;
    for (const track of this.mediaStream?.getTracks() ?? []) track.stop();
    this.mediaStream = undefined;
  }
}
